import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { BiCalendar, BiUser, BiTime, BiArrowBack, BiShare, BiCheck, BiDownload, BiBookOpen, BiImage } from 'react-icons/bi';
import { useTranslations } from '../../locales/useTranslations';
import FadeInAnimation from '../Common/FadeInAnimation';

const FALLBACK_IMAGE = '/images/blog/default-blog.webp';

const BlogDetail = ({ post }) => {
    const { t } = useTranslations();
    const [tableOfContents, setTableOfContents] = useState(post?.tableOfContents || []);
    const [processedContent, setProcessedContent] = useState(post?.content || '');
    const [shareSuccess, setShareSuccess] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [activeHeading, setActiveHeading] = useState('');
    const [imgSrc, setImgSrc] = useState(post?.image || FALLBACK_IMAGE);
    const [hasImageError, setHasImageError] = useState(false);
    const contentRef = useRef(null);
    const printRef = useRef(null);

    // Prüfe ob das Bild ein SVG ist (basierend auf URL)
    const isSvgImage = imgSrc?.toLowerCase().includes('.svg');

    const handleImageError = () => {
        if (!hasImageError) {
            setHasImageError(true);
            setImgSrc(FALLBACK_IMAGE);
        }
    };

    useEffect(() => {
        // Wenn bereits tableOfContents von Server (SSR/SSG) vorhanden, nur activeHeading setzen
        if (post?.tableOfContents?.length) {
            setActiveHeading(post.tableOfContents[0].id);
            return;
        }
        // Client-seitige Extraktion (z.B. bei Navigation über Store)
        if (post?.content && typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(post.content, 'text/html');
                const headings = doc.querySelectorAll('h2, h3');
                const toc = [];
                headings.forEach((heading, index) => {
                    const id = `heading-${index}`;
                    heading.setAttribute('id', id);
                    toc.push({ id, text: heading.textContent, level: heading.tagName.toLowerCase() });
                });
                setTableOfContents(toc);
                setProcessedContent(doc.body.innerHTML);
                if (toc.length > 0) setActiveHeading(toc[0].id);
            } catch {
                setProcessedContent(post.content);
            }
        }
    }, [post?.content, post?.tableOfContents]);


    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: post.title, text: post.excerpt, url: window.location.href });
            } catch {
                copyToClipboard();
            }
        } else {
            copyToClipboard();
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setShareSuccess(true);
            setTimeout(() => setShareSuccess(false), 2000);
        });
    };

    const shouldUsePrintDialog = () => {
        if (typeof window === 'undefined') return false;
        const ua = navigator.userAgent;
        // iOS: iPhone, iPad, iPod (iPad im Desktop-Modus meldet sich als MacIntel mit maxTouchPoints)
        const isIOS = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const isMobile = /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua) || window.innerWidth < 768;
        return isIOS || isMobile;
    };

    const handlePdfExport = async () => {
        if (typeof window === 'undefined' || !printRef.current) return;
        setIsExporting(true);

        // PDF-Only Elemente vor dem Export sichtbar machen
        const pdfElements = printRef.current.querySelectorAll('.pdf-only');
        pdfElements.forEach(el => el.style.display = 'block');

        try {
            // Auf iOS und mobilen Geräten: window.print() nutzen (nutzer wählt "Als PDF speichern")
            // html2pdf.js öffnet auf iOS einen neuen Tab und funktioniert nicht zuverlässig
            if (shouldUsePrintDialog()) {
                window.print();
                return;
            }

            const html2pdf = (await import('html2pdf.js')).default;
            await html2pdf().set({
                margin: [15, 15, 20, 15],
                filename: `${post.slug || 'blog-artikel'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    letterRendering: true,
                    scrollY: 0,
                    windowWidth: 800
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: {
                    mode: ['css', 'legacy']
                }
            }).from(printRef.current).save();
        } catch {
            window.print();
        } finally {
            // PDF-Only Elemente wieder verstecken
            pdfElements.forEach(el => el.style.display = 'none');
            setIsExporting(false);
        }
    };

    const scrollToHeading = (id) => {
        const element = document.getElementById(id);
        if (element) {
            setActiveHeading(id);
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (!post) return null;

    return (
        <div className="blog-detail-wrapper">
            {/* Header */}
            <section className="blog-header no-print">
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <FadeInAnimation direction="up" delay={50}>
                        <div className="blog-category-badge">{post.category}</div>
                        <h1 className="blog-detail-title">{post.title}</h1>
                        <div className="blog-meta">
                            <div className="blog-author">
                                <div className="blog-author-avatar">
                                    <BiUser style={{ color: 'white', fontSize: '1.2rem' }} />
                                </div>
                                <span>{post.author}</span>
                            </div>
                            <div className="blog-meta-item">
                                <BiCalendar />
                                {post.date}
                            </div>
                            <div className="blog-meta-item">
                                <BiTime />
                                {post.readTime} {t('blog_min_read')}
                            </div>
                        </div>
                    </FadeInAnimation>
                </div>
            </section>

            {/* Featured Image */}
            <div className="blog-image-container no-print">
                <div className="container">
                    <div className={`blog-image-wrapper ${isSvgImage ? 'blog-image-wrapper--contain' : ''}`}>
                        {hasImageError && imgSrc === FALLBACK_IMAGE ? (
                            <div className="blog-image-fallback">
                                <BiImage />
                                <span>Bild nicht verfügbar</span>
                            </div>
                        ) : (
                            <Image
                                src={imgSrc}
                                alt={post.title}
                                fill
                                sizes="(max-width: 1200px) 100vw, 1200px"
                                style={{ objectFit: isSvgImage ? 'contain' : 'cover', objectPosition: 'center center' }}
                                onError={handleImageError}
                                priority
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <section className="blog-content-section">
                <div className="blog-detail-page__decoration blog-detail-page__decoration--top-right" />
                <div className="blog-detail-page__decoration blog-detail-page__decoration--bottom-left" />

                <div className="container">
                    <div className="row align-items-start">
                        {/* Main Content */}
                        <div className="col-lg-8 order-2 order-lg-1">
                            {/* Article Content */}
                            <div ref={printRef} className="pdf-container">
                                <div className="pdf-only pdf-header">
                                    <div className="pdf-branding">
                                        <img src="/images/logo.png" alt="Simplify Data" className="pdf-logo" />
                                        <div className="pdf-company-info">
                                            <span className="pdf-company-name">Simplify Data GmbH<br></br></span>
                                            <span className="pdf-company-url">www.simplify-data.de</span>
                                        </div>
                                    </div>
                                    <div className="pdf-divider"></div>
                                    <h1 className="pdf-title">{post.title}</h1>
                                    <p className="pdf-meta">{post.author} • {post.date} • {post.readTime} min Lesezeit</p>
                                    <div className="pdf-divider pdf-divider--thin"></div>
                                </div>
                                <div
                                    ref={contentRef}
                                    className="blog-content"
                                    dangerouslySetInnerHTML={{ __html: processedContent }}
                                />
                                <div className="pdf-only pdf-footer">
                                    <div className="pdf-divider"></div>
                                    <div className="pdf-footer-content">
                                        <span>© {new Date().getFullYear()} Simplify Data GmbH </span>
                                        <span>www.simplify-data.de</span>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="blog-footer no-print">
                                <Link href="/blog" className="blog-back-link">
                                    <BiArrowBack />
                                    {t('blog_back_to_list')}
                                </Link>
                            </div>
                        </div>

                        {/* Sidebar with Table of Contents */}
                        <div className="col-lg-4 order-1 order-lg-2 blog-detail__sidebar-col">
                            <div className="blog-detail__sidebar no-print">
                                {/* Table of Contents */}
                                {tableOfContents.length > 0 && (
                                    <div className="blog-detail__toc-card">
                                        <div className="blog-detail__toc-header">
                                            <div className="blog-detail__toc-icon">
                                                <BiBookOpen />
                                            </div>
                                            <h3>{t('blog_table_of_contents')}</h3>
                                        </div>
                                        <nav className="blog-detail__toc-nav">
                                            <ul className="blog-detail__toc-list">
                                                {tableOfContents.map((item, index) => (
                                                    <li
                                                        key={item.id}
                                                        className={`blog-detail__toc-item blog-detail__toc-item--${item.level} ${activeHeading === item.id ? 'blog-detail__toc-item--active' : ''}`}
                                                        onClick={() => scrollToHeading(item.id)}
                                                    >
                                                        <span className="blog-detail__toc-indicator"></span>
                                                        <span className="blog-detail__toc-number">
                                                            {item.level === 'h2' ? `${tableOfContents.filter((t, i) => i <= index && t.level === 'h2').length}.` : '–'}
                                                        </span>
                                                        <span className="blog-detail__toc-text">{item.text}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </nav>
                                    </div>
                                )}

                                {/* Actions Card */}
                                <div className="blog-detail__actions-card">
                                    <h3>{t('blog_actions_title') || 'Aktionen'}</h3>

                                    {/* PDF Export Button */}
                                    <button
                                        onClick={handlePdfExport}
                                        disabled={isExporting}
                                        className={`blog-detail__action-btn ${isExporting ? 'blog-detail__action-btn--disabled' : ''}`}
                                    >
                                        <BiDownload />
                                        {isExporting ? t('blog_exporting_pdf') : t('blog_export_pdf')}
                                    </button>

                                    {/* Share Button */}
                                    <button
                                        onClick={handleShare}
                                        className={`blog-detail__action-btn blog-detail__action-btn--secondary ${shareSuccess ? 'blog-detail__action-btn--success' : ''}`}
                                    >
                                        {shareSuccess ? (
                                            <><BiCheck /> {t('blog_link_copied')}</>
                                        ) : (
                                            <><BiShare /> {t('blog_share_article')}</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogDetail;
